import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatErr, setAuthToken } from "@/lib/api";
import { colonyApi, setColonyAuthToken } from "@/lib/colonyApi";
import { useAuth } from "@/contexts/AuthContext";
import { useColonyAuth } from "@/contexts/ColonyAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";
import { Home, Play, Building2, Hammer, HomeIcon } from "lucide-react";
import { useColonyBrand } from "@/pages/colony/useColonyBrand";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { getDeviceToken, saveDeviceToken, rememberLoginDevice } from "@/lib/trustedDeviceStorage";
import { useManagementLoginLink } from "@/lib/useManagementLoginLink";
import {
  portalDestination,
  storePendingManagementDeepLink,
} from "@/lib/managementDeepLink";

const HERO_IMG = "/images/bungalow-colony-hero.jpg";

function GoogleGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" className="mr-2" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.8 32.8 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.1 8 3l5.7-5.7C34 6.3 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3.1 0 5.9 1.1 8 3l5.7-5.7C34 6.3 29.3 4 24 4c-7.7 0-14.4 4.4-17.7 10.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.1c-2 1.4-4.5 2.3-7.2 2.3-5.4 0-9.8-3.2-11.3-7.9l-6.5 5C9.4 39.5 16.1 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-2.1 3.7-3.9 4.9l6.2 5.1C40.9 35 44 30 44 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}

/**
 * Unified Management USA NJ sign-in — presented as the branded
 * "Colony login" page. Behind the scenes the form tries the mgmt-admin
 * endpoint first, then the worker endpoint, then the colony endpoint,
 * so a single URL serves all three identities without the user having
 * to pick a portal. Landing page depends on the identity:
 *   - admin/secretary → /managementusa/admin
 *   - worker          → /worker
 *   - colony contact  → /colonies
 */
export default function SharedMgmtLogin() {
  useColonyBrand();
  const { user, refresh } = useAuth();
  const { user: colonyUser, refresh: refreshColony } = useColonyAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null); // { challenge_id, email_hint, kind: 'mgmt'|'worker' }
  const [code, setCode] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [demoOpen, setDemoOpen] = useState(false);

  const deepLink = useManagementLoginLink();
  const destinationFor = useCallback(
    (home) => portalDestination(home, deepLink.next, deepLink.ticket),
    [deepLink.next, deepLink.ticket],
  );

  // Already signed in? Bounce straight back to the portal (replace) so the
  // phone "back" button can never strand someone on this sign-in page.
  useEffect(() => {
    if (deepLink.next.startsWith("/managementusa/admin") && !user) return;
    if (deepLink.next.startsWith("/colonies") && !colonyUser) return;
    if (deepLink.next.startsWith("/colonies") && colonyUser) {
      navigate(destinationFor("/colonies"), { replace: true });
      return;
    }
    if (user) {
      const home = user.role === "worker" || user.is_worker === true
        ? "/worker"
        : user.role === "driver" ? "/driver" : "/managementusa/admin";
      navigate(destinationFor(home), { replace: true });
    } else if (colonyUser) {
      navigate(destinationFor("/colonies"), { replace: true });
    }
  }, [user, colonyUser, navigate, destinationFor, deepLink.next]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  const finalizePool = useCallback(async (data, homePath) => {
    rememberLoginDevice("staff", email, data);
    setAuthToken(data.token);
    await refresh();
    toast.success(`Welcome, ${data.user?.name || data.user?.email}`);
    navigate(destinationFor(homePath), { replace: true });
  }, [refresh, navigate, destinationFor, email]);

  const finalizeColony = useCallback(async (data) => {
    rememberLoginDevice("colony", email, data);
    setColonyAuthToken(data.token);
    await refreshColony();
    toast.success(`Welcome, ${data.user?.name || data.user?.email}`);
    navigate(destinationFor("/colonies"), { replace: true });
  }, [refreshColony, navigate, destinationFor, email]);

  const isInvalid = (err) => err?.response?.status === 401;

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    const staffDeviceToken = getDeviceToken("staff", email);
    const colonyDeviceToken = getDeviceToken("colony", email);
    try {
      // A return link is a destination hint, never an account-store selector.
      // Use the same role-gated sign-in sequence as the plain login address.
      // 1) Try admin/secretary first — the majority of admin sign-ins
      //    happen here, and the endpoint returns a challenge_required
      //    payload we need to route into the code-entry view.
      try {
        const { data } = await api.post("/mgmt/auth/login", {
          email, password, device_token: staffDeviceToken,
        });
        if (data.challenge_required) {
          setChallenge({ ...data, kind: "mgmt", login_email: email });
          setCode(""); setResendIn(30);
          toast.message(`We emailed a verification code to ${data.email_hint}`);
          return;
        }
        return finalizePool(data, "/managementusa/admin");
      } catch (err) {
        if (!isInvalid(err)) throw err;
      }
      // 2) Try worker
      try {
        const { data } = await api.post("/worker/auth/login", {
          email, password, device_token: staffDeviceToken,
        });
        if (data.challenge_required) {
          setChallenge({ ...data, kind: "worker", login_email: email });
          setCode(""); setResendIn(30);
          toast.message(`We emailed a verification code to ${data.email_hint}`);
          return;
        }
        return finalizePool(data, "/worker");
      } catch (err) {
        if (!isInvalid(err)) throw err;
      }
      // 3) Try colony contact
      try {
        const { data } = await colonyApi.post("/colony/auth/login", { email, password, device_token: colonyDeviceToken });
        if (data.challenge_required) {
          setChallenge({ ...data, kind: "colony", login_email: email });
          setCode(""); setResendIn(30);
          toast.message(`We emailed a verification code to ${data.email_hint}`);
          return;
        }
        return finalizeColony(data);
      } catch (err) {
        if (!isInvalid(err)) throw err;
      }
      toast.error("Invalid email or password");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = useCallback(async (codeValue) => {
    if (!challenge || !codeValue) return;
    setLoading(true);
    try {
      const path = challenge.kind === "worker" ? "/worker/auth/verify-login-code"
        : challenge.kind === "colony" ? "/colony/auth/verify-login-code"
        : "/mgmt/auth/verify-login-code";
      const client = challenge.kind === "colony" ? colonyApi : api;
      const { data } = await client.post(path, {
        challenge_id: challenge.challenge_id,
        code: codeValue,
        device_token: getDeviceToken(challenge.kind === "colony" ? "colony" : "staff", challenge.login_email),
      });
      saveDeviceToken(challenge.kind === "colony" ? "colony" : "staff", challenge.login_email, data.device_token);
      if (challenge.kind === "colony") await finalizeColony(data);
      else await finalizePool(data, challenge.kind === "worker" ? "/worker" : "/managementusa/admin");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [challenge, finalizeColony, finalizePool]);

  const submitCode = async (e) => {
    e.preventDefault();
    await verifyCode(code.trim());
  };

  // Auto-verify the moment a full 8-character code is typed or pasted —
  // no need to press the button when the code is correct. Guarded so the
  // exact same value never re-fires (each server attempt is budgeted).
  const CODE_LEN = 8;
  const lastTriedCode = useRef("");
  const onCodeChange = (raw) => {
    const v = raw.replace(/\s/g, "");
    setCode(v);
    if (v.length === CODE_LEN && !loading && lastTriedCode.current !== v) {
      lastTriedCode.current = v;
      verifyCode(v);
    }
  };

  const resendCode = async () => {
    if (!challenge) return;
    setLoading(true);
    try {
      const path = challenge.kind === "worker" ? "/worker/auth/login"
        : challenge.kind === "colony" ? "/colony/auth/login"
        : "/mgmt/auth/login";
      const client = challenge.kind === "colony" ? colonyApi : api;
      const { data } = await client.post(path, {
        email, password,
        device_token: getDeviceToken(challenge.kind === "colony" ? "colony" : "staff", challenge.login_email || email),
      });
      if (data.challenge_required) {
        setChallenge({ ...data, kind: challenge.kind, login_email: challenge.login_email || email });
        setCode(""); setResendIn(30);
        toast.success(`A new code was sent to ${data.email_hint}`);
      } else {
        if (challenge.kind === "colony") await finalizeColony(data);
        else await finalizePool(data, challenge.kind === "worker" ? "/worker" : "/managementusa/admin");
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    storePendingManagementDeepLink(deepLink);
    // Unified Google flow — the callback resolves colony contacts,
    // workers, AND mgmt admins and routes each to their portal.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR
    // REDIRECT URLS, THIS BREAKS THE AUTH.
    const redirectUrl = window.location.origin + "/colonies/auth-callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left hero */}
      <div className="relative hidden md:block">
        <img src={HERO_IMG} alt="Bungalow retreat" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-emerald-950/50" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-baseline gap-2">
            <Home className="w-6 h-6 mr-1" />
            <span className="text-2xl font-black tracking-tighter font-display">BUNGALOW</span>
            <span className="text-lg font-light tracking-wide">PORTAL</span>
          </div>
          <div>
            <p className="overline text-white/70 mb-4">Colony access</p>
            <h1 className="font-display text-5xl font-black tracking-tighter leading-none">
              Your requests.<br />
              Handled fast.<br />
              One place.
            </h1>
            <p className="mt-6 text-white/80 max-w-md leading-relaxed">
              File service requests with photos and track them to completion — day or night.
            </p>
          </div>
          <div className="text-xs text-white/60 font-mono">Management USA NJ</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="md:hidden flex items-baseline gap-2 mb-6">
            <Home className="w-5 h-5 text-emerald-700" />
            <span className="text-2xl font-black tracking-tighter text-emerald-800 font-display">BUNGALOW</span>
            <span className="text-lg font-light tracking-wide">PORTAL</span>
          </div>
          <p className="overline mb-2">Management &amp; colony access</p>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-8" data-testid="mgmt-signin-title">
            Sign in
          </h2>

          {!challenge ? (
            <>
              <form onSubmit={handleLogin} className="space-y-4" data-testid="mgmt-signin-form">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11 rounded-sm"
                    data-testid="mgmt-signin-email"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <PasswordInput
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 rounded-sm"
                    data-testid="mgmt-signin-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="mgmt-signin-submit"
                >
                  {loading ? "Signing in…" : "Sign in"}
                </Button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
                <div className="relative flex justify-center text-xs uppercase tracking-wider">
                  <span className="bg-background px-3 text-muted-foreground">or</span>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full h-11 rounded-sm"
                onClick={handleGoogleSignIn}
                data-testid="mgmt-signin-google"
              >
                <GoogleGlyph /> Continue with Google
              </Button>

              {/* Public interactive demo — one click, no signup. Signs
                  the visitor into a sandboxed account whose data resets
                  every hour. Non-blocking: a page load never fetches
                  the demo status; we only load it when the dialog opens. */}
              <div className="mt-3 rounded-sm border border-emerald-200 bg-emerald-50/60 p-3 text-center">
                <div className="text-xs uppercase tracking-widest text-emerald-800 font-semibold">
                  New here?
                </div>
                <p className="text-xs text-emerald-900/80 mt-1 mb-2">
                  Try the full portal without signing up — data auto-resets every hour.
                </p>
                <Button
                  variant="outline"
                  className="w-full h-10 rounded-sm border-emerald-400 text-emerald-800 hover:bg-emerald-100"
                  onClick={() => setDemoOpen(true)}
                  data-testid="mgmt-signin-demo"
                >
                  <Play className="w-4 h-4 mr-2" /> See a live demo
                </Button>
              </div>

              <div className="mt-8 text-center text-sm text-muted-foreground">
                <Link
                  to="/managementusa/forgot-password"
                  className="underline hover:text-foreground"
                  data-testid="mgmt-signin-forgot-link"
                >
                  Forgot your password?
                </Link>
              </div>
            </>
          ) : (
            <form onSubmit={submitCode} className="space-y-4" data-testid="mgmt-signin-code-form">
              <p className="text-sm text-muted-foreground">
                We emailed a one-time code to <b>{challenge.email_hint}</b>. Codes are case-sensitive
                and expire in 10 minutes.
              </p>
              <div>
                <Label>Verification code</Label>
                <Input
                  value={code}
                  onChange={(e) => onCodeChange(e.target.value)}
                  required
                  autoFocus
                  maxLength={8}
                  className="h-11 rounded-sm font-mono tracking-widest"
                  data-testid="mgmt-signin-code-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !code}
                className="w-full h-11 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="mgmt-signin-code-submit"
              >
                {loading ? "Verifying…" : "Verify & sign in"}
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setChallenge(null); setCode(""); }}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="mgmt-signin-code-cancel"
                >
                  Use a different email
                </button>
                <button
                  type="button"
                  onClick={resendCode}
                  disabled={loading || resendIn > 0}
                  className="text-emerald-700 hover:text-emerald-800 disabled:opacity-50"
                  data-testid="mgmt-signin-code-resend"
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </button>
              </div>
            </form>
          )}
          {!challenge && <div className="mt-5 border-t border-border pt-5"><Button asChild variant="outline" className="w-full h-11 rounded-lg"><Link to="/managementusa/signup" data-testid="management-home-signup">New colony or manager? Sign up</Link></Button></div>}
        </div>
      </div>
      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}

/** One-click demo picker. Signs the visitor in as an admin, worker/manager, or
 * colony contact using the public `/demo/login` endpoint. Refuses to
 * fire twice while a request is in flight so an impatient visitor can't
 * cross-issue two tokens. */
function DemoDialog({ open, onOpenChange }) {
  const { refresh } = useAuth();
  const { refresh: refreshColony } = useColonyAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null); // null | "admin" | "worker" | "worker_manager" | "colony"
  const [workerManager, setWorkerManager] = useState(false);

  useEffect(() => {
    if (!open) setWorkerManager(false);
  }, [open]);

  const start = async (role) => {
    setBusy(role);
    try {
      const { data } = await api.post("/demo/login", { role });
      if (role === "colony") {
        setColonyAuthToken(data.token);
        await refreshColony();
      } else {
        setAuthToken(data.token);
        await refresh();
      }
      toast.success(role === "worker_manager" ? "Welcome to the Worker Manager demo" : `Welcome to the ${role} demo`);
      onOpenChange(false);
      nav(data.redirect || "/managementusa/admin", { replace: true });
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const roles = [
    {
      key: "admin",
      icon: Building2,
      title: "Admin",
      subtitle: "Manage everything",
      body: "Colonies, expenses, payroll, invoices, tickets, drivers — full portal.",
    },
    {
      key: "worker",
      icon: Hammer,
      title: "Worker",
      subtitle: "Field crew view",
      body: "See assigned tickets, reply with photos, mark work done, log expenses.",
    },
    {
      key: "colony",
      icon: HomeIcon,
      title: "Colony",
      subtitle: "Customer-facing",
      body: "File service requests, follow status, view your statement.",
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeTestId="demo-dialog-close" className="max-w-lg" data-testid="demo-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Try the portal — pick a role</DialogTitle>
          <DialogDescription>
            You&apos;ll be signed straight into a sandbox account. The demo data resets automatically every hour, so poke around freely.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 mt-2">
          {roles.map(({ key, icon: Icon, title, subtitle, body }) => {
            const isWorker = key === "worker";
            const selectedRole = isWorker && workerManager ? "worker_manager" : key;
            return (
              <div
                key={key}
                className={`relative text-left rounded-sm border p-3 flex items-start gap-3 transition-colors hover:border-emerald-500 hover:bg-emerald-50/40 focus-within:ring-2 focus-within:ring-emerald-500 ${isWorker && workerManager ? "border-emerald-300 bg-emerald-50" : "border-border"} ${busy !== null ? "opacity-60" : ""}`}
                data-testid={`demo-role-card-${key}`}
              >
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => start(selectedRole)}
                  className="absolute inset-0 z-0 rounded-sm disabled:cursor-wait focus-visible:outline-none"
                  aria-label={`Open ${isWorker ? "Worker Portal" : title} demo${isWorker && workerManager ? " as Worker Manager" : ""}`}
                  data-testid={`demo-start-${key}`}
                />
                <Icon className="relative z-10 pointer-events-none w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                <div className="relative z-10 pointer-events-none flex-1 min-w-0">
                  <div className="font-semibold text-sm" data-testid={`demo-role-${key}-title`}>
                    {isWorker ? "Worker Portal" : title} <span className="text-xs text-muted-foreground font-normal ml-1">· {subtitle}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{body}</div>
                </div>
                {isWorker && (
                  <div className="relative z-20 flex flex-col items-center gap-1 shrink-0 ml-1">
                    <span className="text-[10px] font-bold uppercase text-emerald-700" data-testid="demo-worker-manager-label">Manager</span>
                    <Switch
                      checked={workerManager}
                      onCheckedChange={setWorkerManager}
                      disabled={busy !== null}
                      className="data-[state=checked]:bg-emerald-600"
                      aria-label="Use Worker Manager demo"
                      data-testid="demo-worker-manager-switch"
                    />
                  </div>
                )}
                {busy === selectedRole && <span className="relative z-10 pointer-events-none text-xs text-emerald-700 font-mono">Loading…</span>}
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          Prefer a real account? Close this and use the sign-in form above.
        </p>
      </DialogContent>
    </Dialog>
  );
}
